import { LinkType } from "../../types/baseTypes";
import styles from "./Content.module.scss";
import { ExtendedLink, ExtendedLinkType } from "../ExtededLink/ExtendedLink";
import { passACFLink } from "@/utils/passACFLink";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { ContentTag } from "@/components/ContentTag/ContentTag";
import { Headline, HeadlineTagType } from "@/components/Headline/Headline";
import { Button, ButtonTypes } from "../Button/Button";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG";

interface ContentBaseProps {
  headline_html_tag: HeadlineTagType;
  headline: string;
  headline_text_align_class: "has-text-centered" | "has-text-left" | "";
  headlineExtraClass?: string;
  text_align_class: "has-text-centered" | "has-text-left" | "";
  text: string;
  button_type?: ButtonTypes;
  content_two_columns?: boolean;
  text_2?: string;
  overline?: string;
  subline?: string;
  link_extended?: ExtendedLinkType | false;
  link_2_extended?: ExtendedLinkType | false;
  isQuote?: boolean;
  quoteMarkOpactity?: number;
  source?: string;
  quote_align_class?: "has-text-left" | "has-text-centered" | "has-text-right";
  showQuotationMarks?: boolean;
  isHeader?: boolean;
  textSize?: "big-text" | "small-text";
  frameless?: boolean;
  contentExtraClass?: string;
}

// Conditional helpers for `link` and `link_aria_label`
type LinkProps =
  | { link: LinkType } // If `link` is set, `link_aria_label` is required
  | { link: false }; // If `link` is false, `link_aria_label` is disallowed

type Link2Props =
  | { link_2: LinkType } // If `link_2` is set, `link_2_aria_label` is required
  | { link_2?: false }; // If `link_2` is false or omitted, `link_2_aria_label` is not required

// Combine the base and conditional props
type ContentProps = ContentBaseProps & LinkProps & Link2Props;

export const Content = (props: ContentProps) => {
  const { getIcon } = useGlobalContext();

  const {
    text_align_class,
    headline_html_tag,
    headline,
    headline_text_align_class,
    text,
    link,
    button_type = "primary",
    content_two_columns = false,
    text_2,
    link_2,
    overline,
    subline,
    link_extended,
    link_2_extended,
    isQuote = false,
    quoteMarkOpactity,
    showQuotationMarks,
    quote_align_class,
    source,
    isHeader = false,
    headlineExtraClass = "",
    textSize = "big-text",
    frameless = false,
    contentExtraClass,
  } = props;
  return (
    <div className={`${styles["content"]} ${contentExtraClass ?? ""}`}>
      {isQuote && showQuotationMarks && (
        <div
          className={styles["quote-icon"] + " quote-icon"}
          style={{ opacity: quoteMarkOpactity }}
        >
          <CustomSVG
            width={81}
            height={52}
            src={getIcon("quote")}
            wrapper={"span"}
            alt="quote"
          />
        </div>
      )}
      {headline && headline_html_tag && (
        <Headline
          headline_html_tag={headline_html_tag}
          headline={headline}
          extraClass={`${headlineExtraClass} ${isQuote ? "quotes-banner-title" : ""} ${headline_text_align_class}`}
          overline={overline}
          subline={subline}
          isQuote={isQuote}
          wrapInSection={frameless}
          disableAnimation
        />
      )}
      {content_two_columns ? (
        <div className={styles["two-columns"]}>
          <div className={styles["two-columns-content"]}>
            {text && (
              <ContentTag
                content={text}
                extraClass={`content ${text_align_class}`}
              />
            )}
            {link && link.url && (
              <Button
                type={button_type}
                {...passACFLink(props, "link")}
                className={`${text_align_class === "has-text-centered" ? "mx-auto" : ""}`}
              >
                {link.title}
              </Button>
            )}

            {link_extended && (
              <ExtendedLink
                extendedLink={link_extended}
                btnType={button_type}
                extraClass={`${text_align_class === "has-text-centered" ? "mx-auto" : ""}`}
              />
            )}
          </div>
          <div className={styles["two-columns-content"]}>
            {text_2 && (
              <ContentTag
                content={text_2}
                extraClass={`content ${text_align_class}`}
              />
            )}

            {link_2 && link_2.url && (
              <Button
                type={button_type}
                {...passACFLink(props, "link_2")}
                className={`${text_align_class === "has-text-centered" ? "mx-auto" : ""}`}
              >
                {link_2.title}
              </Button>
            )}

            {link_2_extended && (
              <ExtendedLink
                extendedLink={link_2_extended}
                btnType={button_type}
                extraClass={`${text_align_class === "has-text-centered" ? "mx-auto" : ""}`}
              />
            )}
          </div>
        </div>
      ) : (
        <>
          {text && (
            <ContentTag
              isQuote={isQuote}
              content={text}
              extraClass={`content ${text_align_class} ${isQuote ? (textSize === "big-text" ? "inherit-parent quotes-banner-text-big " : "inherit-parent quotes-banner-text-small ") : ""} ${isHeader ? "intro-text" : ""}`}
            />
          )}

          {isQuote && source && (
            <div className={`${quote_align_class}`}>
              <span className="quotes-source-position">{source}</span>
            </div>
          )}
          {link && link.url && (
            <Button
              type={button_type}
              href={link.url}
              target={link.target}
              aria-label={`${link.title}`}
              className={`${text_align_class === "has-text-centered" ? "mx-auto" : ""}`}
            >
              {link.title}
            </Button>
          )}
          {link_extended && (
            <ExtendedLink
              extendedLink={link_extended}
              btnType={button_type}
              extraClass={`${text_align_class === "has-text-centered" ? "mx-auto" : ""}`}
            />
          )}
        </>
      )}
    </div>
  );
};
